import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key, Mail, Lock, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChangePin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email + OTP, 2: Change PIN
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    oldPin: '',
    newPin: '',
    confirmPin: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/otp/send`, {
        email: formData.email
      });
      
      if (response.data.success) {
        setOtpSent(true);
        toast.success('OTP sent to your email!');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      if (error.response?.status === 403) {
        setError('This email is not authorized');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/otp/verify`, {
        email: formData.email,
        otp: formData.otp
      });
      
      if (response.data.valid) {
        setStep(2);
        toast.success('Email verified successfully!');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error.response?.status === 401) {
        setError('Invalid OTP');
      } else if (error.response?.status === 400) {
        setError('OTP has expired. Please request a new one.');
        setOtpSent(false);
        setFormData(prev => ({ ...prev, otp: '' }));
      } else {
        setError('Failed to verify OTP');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChange = async (e) => {
    e.preventDefault();
    setError('');

    // Validate new PIN
    if (formData.newPin.length !== 4) {
      setError('New PIN must be 4 digits');
      return;
    }

    if (formData.newPin !== formData.confirmPin) {
      setError('New PIN and confirmation do not match');
      return;
    }

    if (formData.oldPin === formData.newPin) {
      setError('New PIN must be different from old PIN');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/pin/change`, {
        email: formData.email,
        otp: formData.otp,
        oldPin: formData.oldPin,
        newPin: formData.newPin
      });

      if (response.data.success) {
        setSuccess(true);
        toast.success('PIN changed successfully!');
        
        // Clear session storage
        sessionStorage.clear();
        
        // Redirect to admin page after 2 seconds
        setTimeout(() => {
          navigate('/admin/chats');
        }, 2000);
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      if (error.response?.status === 403) {
        setError('Unauthorized email address');
      } else if (error.response?.status === 401) {
        setError('Incorrect old PIN or invalid OTP');
      } else {
        setError('Failed to change PIN. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-none shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">PIN Changed Successfully!</h2>
            <p className="text-gray-600 mb-6">Your admin PIN has been updated</p>
            <p className="text-sm text-gray-500">Redirecting to admin panel...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12 px-4">
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Change Admin PIN</h1>
          <p className="text-gray-600">Secure access management</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="ml-2 font-semibold">Verify Email</span>
          </div>
          <div className={`w-16 h-1 mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="ml-2 font-semibold">Change PIN</span>
          </div>
        </div>

        <Card className="border-none shadow-2xl">
          <CardContent className="p-8">
            {step === 1 ? (
              // Step 1: Email + OTP Verification
              <>
                <div className="text-center mb-6">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="text-blue-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
                  <p className="text-gray-600">Enter your authorized email to receive OTP</p>
                </div>

                {!otpSent ? (
                  // Email Input Form
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                        className="text-lg border-blue-200 focus:border-blue-500"
                        autoFocus
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                      disabled={isLoading || !formData.email}
                    >
                      {isLoading ? 'Sending...' : (
                        <>
                          <Send size={20} className="mr-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  // OTP Input Form
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                      <p className="text-sm text-blue-800">
                        ✉️ OTP has been sent to your email. Please check your inbox.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="otp">Enter OTP</Label>
                      <Input
                        id="otp"
                        name="otp"
                        type="text"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        required
                        className="text-center text-2xl tracking-widest border-blue-200 focus:border-blue-500"
                        autoFocus
                      />
                      <p className="text-xs text-gray-500 mt-2">OTP expires in 10 minutes</p>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setError('');
                          setFormData(prev => ({ ...prev, otp: '' }));
                        }}
                        variant="outline"
                        className="flex-1 border-gray-300"
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={isLoading || formData.otp.length !== 6}
                      >
                        {isLoading ? 'Verifying...' : 'Verify OTP'}
                      </Button>
                    </div>

                    <Button
                      type="button"
                      onClick={handleSendOTP}
                      variant="ghost"
                      className="w-full text-blue-600"
                      disabled={isLoading}
                    >
                      Resend OTP
                    </Button>
                  </form>
                )}
              </>
            ) : (
              // Step 2: Change PIN
              <>
                <div className="text-center mb-6">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Key className="text-blue-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Set New PIN</h2>
                  <p className="text-gray-600">Choose a secure 4-digit PIN</p>
                </div>

                <form onSubmit={handlePinChange} className="space-y-4">
                  <div>
                    <Label htmlFor="oldPin">Old PIN</Label>
                    <Input
                      id="oldPin"
                      name="oldPin"
                      type="password"
                      value={formData.oldPin}
                      onChange={handleChange}
                      placeholder="Enter current PIN"
                      maxLength={4}
                      required
                      className="text-center text-xl tracking-widest border-blue-200 focus:border-blue-500"
                      autoFocus
                    />
                  </div>

                  <div>
                    <Label htmlFor="newPin">New PIN</Label>
                    <Input
                      id="newPin"
                      name="newPin"
                      type="password"
                      value={formData.newPin}
                      onChange={handleChange}
                      placeholder="Enter new 4-digit PIN"
                      maxLength={4}
                      required
                      className="text-center text-xl tracking-widest border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirmPin">Confirm New PIN</Label>
                    <Input
                      id="confirmPin"
                      name="confirmPin"
                      type="password"
                      value={formData.confirmPin}
                      onChange={handleChange}
                      placeholder="Re-enter new PIN"
                      maxLength={4}
                      required
                      className="text-center text-xl tracking-widest border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={() => {
                        setStep(1);
                        setOtpSent(false);
                        setError('');
                        setFormData(prev => ({ ...prev, otp: '', oldPin: '', newPin: '', confirmPin: '' }));
                      }}
                      variant="outline"
                      className="flex-1 border-gray-300"
                      disabled={isLoading}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={isLoading || formData.oldPin.length !== 4 || formData.newPin.length !== 4 || formData.confirmPin.length !== 4}
                    >
                      {isLoading ? 'Changing...' : 'Change PIN'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-6 border-none shadow-lg bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Lock className="text-blue-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Security Notice</h3>
                <p className="text-sm text-gray-700">
                  Only authorized users can change the admin PIN. An OTP will be sent to your registered email for verification.
                  This ensures secure access control to all admin panels.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePin;
